import {
  isRecord,
  isSessionDraft,
  restoreCreatorState,
  type EventCreatorState,
} from '@project/event-creator';
import { validateEvent, type EventDraft } from '@project/event-format';
import type { IntakeBatch } from '@project/core';

export const RECOVERY_KEY = 'ics-maker-workspace-v1';
export interface WorkspaceRecovery {
  readonly version: 1;
  readonly session: EventCreatorState | null;
  readonly baseline: EventDraft | null;
  readonly collected: IntakeBatch | null;
  readonly editing: string | null;
  readonly runs: number;
  readonly tab: 'form' | 'funnel';
  readonly text: string;
}
const strings = (v: unknown): boolean => Array.isArray(v) && v.every((s) => typeof s === 'string');
function batch(value: unknown): value is IntakeBatch {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.headline === 'string' &&
    isRecord(value.media) &&
    typeof value.media.label === 'string' &&
    ['text', 'image', 'audio', 'document', 'pdf', 'unsupported'].includes(
      String(value.media.kind),
    ) &&
    strings(value.flagged) &&
    strings(value.settled) &&
    Array.isArray(value.forms) &&
    value.forms.every(
      (form: unknown) =>
        isRecord(form) &&
        typeof form.id === 'string' &&
        isSessionDraft(form.draft) &&
        typeof form.confidence === 'number' &&
        Number.isFinite(form.confidence) &&
        strings(form.assumptions) &&
        (form.assumptionCodes === undefined ||
          (Array.isArray(form.assumptionCodes) &&
            form.assumptionCodes.every((code) =>
              ['all-day', 'day-month', 'inherited-date', 'missing-title'].includes(code),
            ))) &&
        Array.isArray(form.flags) &&
        form.flags.every(
          (flag: unknown) =>
            isRecord(flag) &&
            typeof flag.note === 'string' &&
            (flag.kind === 'low-confidence' ||
              (flag.kind === 'duplicate' &&
                isRecord(flag.duplicateOf) &&
                ['batch', 'calendar'].includes(String(flag.duplicateOf.scope)) &&
                typeof flag.duplicateOf.id === 'string' &&
                typeof flag.duplicateOf.title === 'string')),
        ),
    )
  );
}

export function readRecovery(): WorkspaceRecovery | null {
  try {
    const raw: unknown = JSON.parse(sessionStorage.getItem(RECOVERY_KEY) ?? 'null');
    if (
      !isRecord(raw) ||
      raw.version !== 1 ||
      typeof raw.text !== 'string' ||
      !['form', 'funnel'].includes(String(raw.tab)) ||
      typeof raw.runs !== 'number' ||
      !Number.isSafeInteger(raw.runs) ||
      raw.runs < 0 ||
      (raw.baseline !== null && !isSessionDraft(raw.baseline)) ||
      (raw.collected !== null && !batch(raw.collected)) ||
      (raw.editing !== null && typeof raw.editing !== 'string')
    )
      return null;
    const session = raw.session === null ? null : restoreCreatorState(raw.session);
    if (raw.session !== null && !session) return null;
    const collected = raw.collected as IntakeBatch | null;
    if (
      raw.editing !== null &&
      (!session || !collected?.forms.some((form) => form.id === raw.editing))
    )
      return null;
    return {
      ...(raw as unknown as WorkspaceRecovery),
      session,
      collected: collected && {
        ...collected,
        forms: collected.forms.map((form) => ({
          ...form,
          problems: validateEvent(form.draft),
        })),
      },
    };
  } catch {
    return null;
  }
}

export function writeRecovery(value: WorkspaceRecovery): void {
  // Blocked storage or a full quota must never prevent typing or downloading.
  try {
    sessionStorage.setItem(RECOVERY_KEY, JSON.stringify(value));
  } catch {
    /* Best effort. */
  }
}
